import { KYCData } from '../types'
import { User } from '../types/auth'

type KYCConfirmationPayload = Partial<KYCData> & {
  cams_kyc_verified?: 'yes' | 'not_found'
}

type KYCConfirmationSource = Omit<Partial<KYCData>, 'date_of_birth'> & {
  date_of_birth?: unknown
}

const fallbackText = 'Not available'
const fallbackPincode = '000000'

const normalizeKYCName = (value?: unknown): string => String(value || '').trim()

const isValidISODate = (value: string): boolean => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return false

  const [, year, month, day] = match
  const parsed = new Date(`${value}T00:00:00Z`)
  return parsed.getUTCFullYear() === Number(year)
    && parsed.getUTCMonth() + 1 === Number(month)
    && parsed.getUTCDate() === Number(day)
}

export const normalizeKYCDate = (value?: unknown): string => {
  if (!value) return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    return value.toISOString().split('T')[0]
  }

  const trimmed = String(value).trim()
  if (isValidISODate(trimmed)) return trimmed
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const datePart = trimmed.split('T')[0]
    return isValidISODate(datePart) ? datePart : ''
  }

  const slashDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (slashDate) {
    const [, day, month, year] = slashDate
    const datePart = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    return isValidISODate(datePart) ? datePart : ''
  }

  const parsed = new Date(trimmed)
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0]

  return ''
}

export const buildKYCConfirmationPayload = (
  kycData: KYCConfirmationSource | null | undefined,
  user: User | null,
  kycFound: boolean,
  dob?: unknown
): KYCConfirmationPayload => ({
  full_name: normalizeKYCName(kycData?.full_name) || normalizeKYCName(user?.full_name),
  date_of_birth: normalizeKYCDate(dob) || normalizeKYCDate(kycData?.date_of_birth) || normalizeKYCDate(user?.date_of_birth),
  gender: kycData?.gender || undefined,
  father_name: kycData?.father_name || undefined,
  address_line1: kycData?.address_line1 || user?.address || fallbackText,
  address_line2: kycData?.address_line2 || undefined,
  city: kycData?.city || user?.city || fallbackText,
  state: kycData?.state || user?.state || fallbackText,
  pincode: kycData?.pincode || user?.pincode || fallbackPincode,
  country: kycData?.country || 'India',
  annual_income: kycData?.annual_income || undefined,
  occupation: kycData?.occupation || undefined,
  investment_experience: kycData?.investment_experience || undefined,
  risk_tolerance: kycData?.risk_tolerance || undefined,
  cams_kyc_verified: kycFound ? 'yes' : 'not_found',
})
